import React from 'react';
import { STORE_URLS, isStoreLive } from '@/lib/storeUrls';

/**
 * StoreBadges
 *
 * Official Apple App Store + Google Play badges, rendered side-by-side.
 * When STORE_URLS.ios / STORE_URLS.android are empty, the badge displays a
 * dimmed "Coming Soon" overlay and is non-clickable. Once you update
 * /app/frontend/src/lib/storeUrls.js with the published store links, both
 * badges automatically become live, full-color, and clickable.
 *
 * Props:
 *   - size:    'sm' | 'md' (default 'md')
 *   - variant: 'row' | 'stack' (default 'row')
 *   - showLabel: boolean - render small caption above the badges (default true)
 *   - className: extra wrapper classes
 *   - testIdPrefix: prefix for data-testid attributes (default 'store-badges')
 */
export function StoreBadges({
  size = 'md',
  variant = 'row',
  showLabel = true,
  className = '',
  testIdPrefix = 'store-badges',
}) {
  // Apple SVG is 119.66 x 40 (~3:1).  Google PNG is 646x250 (~2.58:1).
  // To make them visually equal in height we set a common height and let
  // widths flow naturally.
  const heights = {
    sm: 'h-9',   // 36px
    md: 'h-11',  // 44px (Apple's recommended minimum touch target)
  };
  const h = heights[size] || heights.md;

  const iosLive = isStoreLive.ios();
  const androidLive = isStoreLive.android();

  const wrapperLayout =
    variant === 'stack'
      ? 'flex flex-col items-center gap-2'
      : 'flex flex-row items-center justify-center gap-3';

  return (
    <div
      data-testid={`${testIdPrefix}-container`}
      className={`w-full ${className}`}
    >
      {showLabel && (
        <p
          className="mb-2 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50"
          data-testid={`${testIdPrefix}-label`}
        >
          Get the app
        </p>
      )}
      <div className={wrapperLayout}>
        <BadgeSlot
          live={iosLive}
          url={STORE_URLS.ios}
          alt="Download Thirrsty.com on the App Store"
          src="/badges/app-store-badge.svg"
          heightClass={h}
          testId={`${testIdPrefix}-ios`}
        />
        <BadgeSlot
          live={androidLive}
          url={STORE_URLS.android}
          alt="Get Thirrsty.com on Google Play"
          src="/badges/google-play-badge.png"
          heightClass={h}
          testId={`${testIdPrefix}-android`}
        />
      </div>
    </div>
  );
}

function BadgeSlot({ live, url, alt, src, heightClass, testId }) {
  if (live) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={alt}
        data-testid={`${testId}-link`}
        className="inline-flex items-center justify-center transition-transform duration-150 hover:scale-[1.04] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F6B24A]/70 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded-md"
      >
        <img
          src={src}
          alt={alt}
          className={`${heightClass} w-auto select-none`}
          draggable={false}
        />
      </a>
    );
  }

  // Coming-soon state - non-interactive
  return (
    <div
      role="img"
      aria-label={`${alt} — coming soon`}
      data-testid={`${testId}-coming-soon`}
      className="relative inline-flex items-center justify-center"
    >
      <img
        src={src}
        alt={alt}
        className={`${heightClass} w-auto select-none opacity-40 grayscale`}
        draggable={false}
      />
      <span
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
        aria-hidden
      >
        <span className="rounded-md bg-black/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#F6B24A] backdrop-blur-sm border border-[#F6B24A]/30">
          Coming Soon
        </span>
      </span>
    </div>
  );
}

export default StoreBadges;
